import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";
import { Plus } from "lucide-react";

import Connections, {
  MeterRecord,
  INITIAL_METERS_DATA,
} from "./tabsComponents/connections/connections";
import ConnectionsHistory, {
  DisconnectedMeterRecord,
  INITIAL_DISCONNECTED_METERS_DATA,
} from "./tabsComponents/connectionsHistory/connectionsHistory";
import {
  AddResourceModal,
  InventoryResource,
  ActiveResource,
} from "./addResourceModal/AddResourceModal";

const getFormattedTodayDate = (): string => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const year = today.getFullYear();
  return `${month}/${day}/${year}`;
};

const getInitialReadingUnit = (service: string): string => {
  switch (service) {
    case "Water":
      return "0 gal";
    case "Electricity":
    case "Solar":
      return "0 kWh";
    case "Gas":
      return "0 ccf";
    case "Hot Water":
      return "0 gal";
    case "Waste Water":
      return "0 gal";
    case "EV":
      return "0 kWh";
    default:
      return "0";
  }
};

const Resource = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeSubTab = searchParams.get("tab") || "connections";

  const [activeMeters, setActiveMeters] =
    useState<MeterRecord[]>(INITIAL_METERS_DATA);
  const [disconnectedMeters, setDisconnectedMeters] = useState<
    DisconnectedMeterRecord[]
  >(INITIAL_DISCONNECTED_METERS_DATA);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const urlMapping = {
    connections: "connections",
    connectionsHistory: "connections-history",
  };

  // Convert activeMeters to ActiveResource format for AddResourceModal
  const modalActiveResources: ActiveResource[] = activeMeters.map((m) => ({
    id: m.id,
    meterId: m.meterId,
    service: m.service,
    serialNo: m.serialNo,
    type: m.type,
    lastReading: m.lastReading,
    readDate: m.readDate,
    installDate: m.installDate,
  }));

  // Handle Attach Resource (Add New path)
  const handleAttachResource = (newResource: InventoryResource) => {
    const todayDate = getFormattedTodayDate();
    const newMeter: MeterRecord = {
      id: String(Date.now()),
      meterId: newResource.resourceId,
      service: newResource.service,
      serialNo: newResource.serialNo,
      type: newResource.type,
      lastReading: getInitialReadingUnit(newResource.service),
      readDate: todayDate,
      installDate: todayDate,
    };
    setActiveMeters((prev) => [newMeter, ...prev]);
  };

  // Handle Update Resource (Update Existing path)
  const handleUpdateResource = (
    oldResource: ActiveResource,
    newResource: InventoryResource,
  ) => {
    const todayDate = getFormattedTodayDate();

    // 1. Move old resource to disconnectedMeters
    const disconnectedMeter: DisconnectedMeterRecord = {
      id: String(Date.now()),
      meterId: oldResource.meterId,
      service: oldResource.service,
      serialNo: oldResource.serialNo,
      type: oldResource.type,
      lastReading: oldResource.lastReading,
      installDate: oldResource.installDate,
      disconnectedDate: todayDate,
    };
    setDisconnectedMeters((prev) => [disconnectedMeter, ...prev]);

    // 2. Add replacement resource to activeMeters & remove old resource
    const replacementMeter: MeterRecord = {
      id: String(Date.now() + 1),
      meterId: newResource.resourceId,
      service: newResource.service,
      serialNo: newResource.serialNo,
      type: newResource.type,
      lastReading: getInitialReadingUnit(newResource.service),
      readDate: todayDate,
      installDate: todayDate,
    };
    setActiveMeters((prev) => [
      replacementMeter,
      ...prev.filter((m) => m.id !== oldResource.id),
    ]);
  };

  const tabComponents = {
    connections: {
      label: "Connections",
      component: <Connections meters={activeMeters} />,
    },
    connectionsHistory: {
      label: "Connections History",
      component: <ConnectionsHistory meters={disconnectedMeters} />,
    },
  };

  const isConnectionsTabActive = activeSubTab !== "connections-history";

  return (
    <div>
      <PageHeader
        title="Resource"
        subtitle="Manage utility resources, active connections, and historical disconnection records"
      >
        {isConnectionsTabActive && (
          <Button onClick={() => setIsAddModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Resource
          </Button>
        )}
      </PageHeader>

      <Tabs
        defaultValue="connections"
        className="w-full"
        tabsListClassName="grid grid-cols-2 w-full mt-3"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        level={1}
      />

      <AddResourceModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        activeResources={modalActiveResources}
        onAttachResource={handleAttachResource}
        onUpdateResource={handleUpdateResource}
      />
    </div>
  );
};

export default Resource;
